import React, { useState, useEffect } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import {
  LIST_USER_HOUSEHOLDS,
  GET_HOUSEHOLD,
  GET_HOUSEHOLD_MEMBERS,
} from '../graphql/queries';
import {
  CREATE_HOUSEHOLD,
  UPDATE_HOUSEHOLD,
  ADD_HOUSEHOLD_MEMBER,
  UPDATE_MEMBER_PROFILE,
  REMOVE_HOUSEHOLD_MEMBER,
} from '../graphql/mutations';

interface HouseholdMember {
  householdId: string;
  userId: string;
  name: string;
  email: string;
  diet: string;
  allergies: string[];
  dislikes: string[];
  preferences: string[];
  spiceLevel: string;
  role: 'owner' | 'member';
}

interface HouseholdSettings {
  location: string;
  weeklyBudget: number;
  mealPrepTime: string;
  dietaryPreferences: string[];
  allergies: string[];
  equipment: string[];
}

const Household: React.FC = () => {
  // State for household data
  const [selectedHousehold, setSelectedHousehold] = useState<string>('');
  const [householdName, setHouseholdName] = useState<string>('');
  const [settings, setSettings] = useState<HouseholdSettings>({
    location: '',
    weeklyBudget: 0,
    mealPrepTime: 'Moderate (30-60 min)',
    dietaryPreferences: [],
    allergies: [],
    equipment: ['Oven', 'Stovetop', 'Microwave'], // Default equipment
  });

  // State for UI
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [members, setMembers] = useState<HouseholdMember[]>([]);

  // GraphQL queries
  const { data: householdsData, refetch: refetchHouseholds } = useQuery(LIST_USER_HOUSEHOLDS);
  const { data: householdData, refetch: refetchHousehold } = useQuery(GET_HOUSEHOLD, {
    variables: { id: selectedHousehold },
    skip: !selectedHousehold,
  });
  const { data: membersData, refetch: refetchMembers } = useQuery(GET_HOUSEHOLD_MEMBERS, {
    variables: { householdId: selectedHousehold },
    skip: !selectedHousehold,
  });

  // GraphQL mutations
  const [createHousehold] = useMutation(CREATE_HOUSEHOLD);
  const [updateHousehold] = useMutation(UPDATE_HOUSEHOLD);
  const [addHouseholdMember] = useMutation(ADD_HOUSEHOLD_MEMBER);
  const [removeMember] = useMutation(REMOVE_HOUSEHOLD_MEMBER);

  // Effects
  useEffect(() => {
    if (householdsData?.listUserHouseholds?.length > 0 && !selectedHousehold) {
      const household = householdsData.listUserHouseholds[0];
      setSelectedHousehold(household.id);
      setHouseholdName(household.name);
    }
  }, [householdsData, selectedHousehold]);

  useEffect(() => {
    if (householdData?.getHousehold) {
      const household = householdData.getHousehold;
      setHouseholdName(household.name);
      setSettings({
        location: household.settings?.location || '',
        weeklyBudget: household.settings?.weeklyBudget || 0,
        mealPrepTime: household.settings?.mealPrepTime || 'Moderate (30-60 min)',
        dietaryPreferences: household.settings?.dietaryPreferences || [],
        allergies: household.settings?.allergies || [],
        equipment: household.settings?.equipment || ['Oven', 'Stovetop', 'Microwave'],
      });
    }
  }, [householdData]);

  useEffect(() => {
    if (membersData?.getHouseholdMembers) {
      setMembers(membersData.getHouseholdMembers);
    }
  }, [membersData]);

  // Handlers
  const handleCreateHousehold = async () => {
    if (!householdName.trim()) {
      alert('Please enter a household name');
      return;
    }

    setLoading(true);
    try {
      const result = await createHousehold({
        variables: {
          input: {
            name: householdName,
            settings: settings,
          },
        },
      });

      if (result.data?.createHousehold) {
        setSelectedHousehold(result.data.createHousehold.id);
        await refetchHouseholds();
        alert('Household created successfully!');
      }
    } catch (error: any) {
      alert(`Failed to create household: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleSavePreferences = async () => {
    if (!selectedHousehold) {
      await handleCreateHousehold();
      return;
    }

    setLoading(true);
    try {
      await updateHousehold({
        variables: {
          id: selectedHousehold,
          input: {
            name: householdName,
            settings: settings,
          },
        },
      });

      await refetchHousehold();
      alert('Preferences saved successfully!');
    } catch (error: any) {
      alert(`Failed to save preferences: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleInviteMember = async () => {
    if (!inviteEmail.trim()) {
      alert('Please enter an email address');
      return;
    }

    if (!selectedHousehold) {
      alert('Please create a household first');
      return;
    }

    setLoading(true);
    try {
      await addHouseholdMember({
        variables: {
          householdId: selectedHousehold,
          email: inviteEmail,
        },
      });

      setInviteEmail('');
      setShowInviteModal(false);
      await refetchMembers();
      alert('Member invited successfully!');
    } catch (error: any) {
      alert(`Failed to invite member: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveMember = async (userId: string) => {
    if (!window.confirm('Are you sure you want to remove this member?')) return;

    try {
      await removeMember({
        variables: {
          householdId: selectedHousehold,
          userId: userId,
        },
      });

      await refetchMembers();
      alert('Member removed successfully');
    } catch (error: any) {
      alert(`Failed to remove member: ${error.message}`);
    }
  };

  const toggleArrayItem = (array: string[], item: string, setter: (value: string[]) => void) => {
    if (array.includes(item)) {
      setter(array.filter(i => i !== item));
    } else {
      setter([...array, item]);
    }
  };

  return (
    <div className="household-page">
      <div className="page-header">
        <h1 className="page-title">Household Management</h1>
        <p className="page-subtitle">Manage your household members and preferences</p>
      </div>

      {/* Household Selection */}
      {householdsData?.listUserHouseholds?.length > 0 && (
        <div className="card">
          <h2 className="card-title">Select Household</h2>
          <select
            value={selectedHousehold}
            onChange={(e) => setSelectedHousehold(e.target.value)}
            className="form-input"
          >
            {householdsData.listUserHouseholds.map((household: any) => (
              <option key={household.id} value={household.id}>
                {household.name}
              </option>
            ))}
          </select>
        </div>
      )}

      {/* Household Settings */}
      <div className="card">
        <h2 className="card-title">Household Settings</h2>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '1rem' }}>
          <div className="form-group">
            <label>Household Name</label>
            <input
              type="text"
              placeholder="e.g., The Smith Family"
              value={householdName}
              onChange={(e) => setHouseholdName(e.target.value)}
              className="form-input"
            />
          </div>
          <div className="form-group">
            <label>Location</label>
            <input
              type="text"
              placeholder="City, State"
              value={settings.location}
              onChange={(e) => setSettings({ ...settings, location: e.target.value })}
              className="form-input"
            />
          </div>
          <div className="form-group">
            <label>Weekly Budget ($)</label>
            <input
              type="number"
              placeholder="150"
              value={settings.weeklyBudget || ''}
              onChange={(e) => setSettings({ ...settings, weeklyBudget: parseFloat(e.target.value) || 0 })}
              className="form-input"
            />
          </div>
          <div className="form-group">
            <label>Meal Prep Time</label>
            <select
              value={settings.mealPrepTime}
              onChange={(e) => setSettings({ ...settings, mealPrepTime: e.target.value })}
              className="form-input"
            >
              <option>Quick (less than 30 min)</option>
              <option>Moderate (30-60 min)</option>
              <option>Leisurely (more than 60 min)</option>
            </select>
          </div>
        </div>
      </div>

      {/* Members */}
      <div className="card">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
          <h2 className="card-title" style={{ margin: 0 }}>Household Members</h2>
          <button
            className="button-primary"
            onClick={() => setShowInviteModal(true)}
            disabled={!selectedHousehold || loading}
            title={!selectedHousehold ? "Please save household settings first" : "Invite a new member"}
          >
            + Invite Member
          </button>
        </div>

        {members.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '2rem', background: '#f9fafb', borderRadius: '0.5rem' }}>
            <div style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>👨‍👩‍👧‍👦</div>
            <p style={{ color: '#6b7280' }}>No members yet. Invite family members to collaborate on meal planning.</p>
          </div>
        ) : (
          <div style={{ display: 'grid', gap: '1rem' }}>
            {members.map((member) => (
              <div
                key={member.userId}
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  padding: '1rem',
                  border: '1px solid #e5e7eb',
                  borderRadius: '0.5rem',
                }}
              >
                <div>
                  <h4 style={{ margin: 0, marginBottom: '0.25rem' }}>{member.name}</h4>
                  <p style={{ margin: 0, color: '#6b7280', fontSize: '0.875rem' }}>
                    {member.email} • {member.role}
                  </p>
                  {member.diet && (
                    <p style={{ margin: '0.25rem 0 0 0', color: '#059669', fontSize: '0.875rem' }}>
                      Diet: {member.diet}
                    </p>
                  )}
                </div>
                {member.role !== 'owner' && (
                  <button
                    onClick={() => handleRemoveMember(member.userId)}
                    className="button-secondary"
                    style={{ fontSize: '0.875rem', padding: '0.5rem 1rem' }}
                  >
                    Remove
                  </button>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Dietary Preferences */}
      <div className="card">
        <h2 className="card-title">Dietary Preferences</h2>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem' }}>
          {['Vegetarian', 'Vegan', 'Gluten-Free', 'Dairy-Free', 'Keto', 'Paleo', 'Low-Carb', 'Pescatarian'].map(diet => (
            <label key={diet} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <input
                type="checkbox"
                checked={settings.dietaryPreferences.includes(diet)}
                onChange={() => toggleArrayItem(
                  settings.dietaryPreferences,
                  diet,
                  (newPrefs) => setSettings({ ...settings, dietaryPreferences: newPrefs })
                )}
              />
              <span>{diet}</span>
            </label>
          ))}
        </div>
      </div>

      {/* Allergies */}
      <div className="card">
        <h2 className="card-title">Allergies & Restrictions</h2>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '1rem' }}>
          {['Peanuts', 'Tree Nuts', 'Milk', 'Eggs', 'Wheat', 'Soy', 'Fish', 'Shellfish'].map(allergy => (
            <label key={allergy} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <input
                type="checkbox"
                checked={settings.allergies.includes(allergy)}
                onChange={() => toggleArrayItem(
                  settings.allergies,
                  allergy,
                  (newAllergies) => setSettings({ ...settings, allergies: newAllergies })
                )}
              />
              <span>{allergy}</span>
            </label>
          ))}
        </div>
      </div>

      {/* Kitchen Equipment */}
      <div className="card">
        <h2 className="card-title">Available Kitchen Equipment</h2>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem' }}>
          {['Oven', 'Stovetop', 'Microwave', 'Instant Pot', 'Slow Cooker', 'Air Fryer', 'Grill', 'Blender'].map(equipment => (
            <label key={equipment} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <input
                type="checkbox"
                checked={settings.equipment.includes(equipment)}
                onChange={() => toggleArrayItem(
                  settings.equipment,
                  equipment,
                  (newEquipment) => setSettings({ ...settings, equipment: newEquipment })
                )}
              />
              <span>{equipment}</span>
            </label>
          ))}
        </div>
      </div>

      {/* Save Button */}
      <div style={{ marginTop: '2rem', textAlign: 'right' }}>
        <button
          className="button-primary"
          onClick={handleSavePreferences}
          disabled={loading}
        >
          {loading ? 'Saving...' : selectedHousehold ? 'Save Preferences' : 'Create Household'}
        </button>
      </div>

      {/* Invite Member Modal */}
      {showInviteModal && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
          }}
          onClick={() => setShowInviteModal(false)}
        >
          <div
            style={{
              backgroundColor: 'white',
              padding: '2rem',
              borderRadius: '0.5rem',
              minWidth: '400px',
              maxWidth: '500px',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h3 style={{ marginTop: 0 }}>Invite Member</h3>
            <p style={{ color: '#6b7280', marginBottom: '1rem' }}>
              Enter the email address of the person you'd like to invite to your household.
            </p>
            
            <div className="form-group">
              <label>Email Address</label>
              <input
                type="email"
                placeholder="member@example.com"
                value={inviteEmail}
                onChange={(e) => setInviteEmail(e.target.value)}
                className="form-input"
                autoFocus
              />
            </div>

            <div style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end', marginTop: '2rem' }}>
              <button
                className="button-secondary"
                onClick={() => setShowInviteModal(false)}
              >
                Cancel
              </button>
              <button
                className="button-primary"
                onClick={handleInviteMember}
                disabled={loading || !inviteEmail.trim()}
              >
                {loading ? 'Inviting...' : 'Send Invite'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Household;
